import { useEffect, useMemo, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@molt/shared';
import { SearchResultsPage } from '@/components/search/SearchResultsPage';
import {
  adjacentProductSearchQueryOptions,
  fullSearchResultsQueryOptions,
  savedSearchesQueryOptions,
  useCreateSavedSearch,
  useDeleteSavedSearch,
  useRenameSavedSearch,
  useRunSavedSearch,
} from '@/lib/search';
import { queryKeys } from '@/lib/query-keys';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  searchSideFilterState,
  withSearchSideFilter,
  type SearchSideFilterState,
} from '@/lib/search-operators';

/**
 * ⚠ `matchDescriptions` is **inverted** against the other two.
 *
 * `includeArchived` and `includeClosedBoards` default to `false` on the wire
 * and are written into the URL only when `true`. `matchDescriptions` defaults
 * to `true` (`searchFullResultsPageQuerySchema`, `packages/shared/src/search.ts`)
 * and is written only when `false`. That is what keeps every URL this route
 * produces today byte-identical — a bookmarked `/search?q=x` still restores
 * with descriptions matched, and the eleven e2e specs that assert on these
 * query strings see no new key.
 */
interface SearchRouteSearch {
  q?: string;
  includeArchived?: boolean;
  includeClosedBoards?: boolean;
  matchDescriptions?: boolean;
}

function stringValue(value: unknown): string | undefined {
  return typeof value === 'string' ? value.trim() : undefined;
}

function booleanValue(value: unknown): boolean | undefined {
  if (value === true || value === 'true') return true;
  if (value === false || value === 'false') return false;
  return undefined;
}

function searchHref(search: SearchRouteSearch): string {
  const params = new URLSearchParams();
  const q = search.q?.trim();
  if (q) params.set('q', q);
  if (search.includeArchived === true) params.set('includeArchived', 'true');
  if (search.includeClosedBoards === true) {
    params.set('includeClosedBoards', 'true');
  }
  if (search.matchDescriptions === false) {
    params.set('matchDescriptions', 'false');
  }
  const query = params.toString();
  return query.length === 0 ? '/search' : `/search?${query}`;
}

export const Route = createFileRoute('/search')({
  validateSearch: (search: Record<string, unknown>): SearchRouteSearch => {
    const q = stringValue(search.q);
    const includeArchived = booleanValue(search.includeArchived);
    const includeClosedBoards = booleanValue(search.includeClosedBoards);
    const matchDescriptions = booleanValue(search.matchDescriptions);
    return {
      ...(q === undefined || q.length === 0 ? {} : { q }),
      ...(includeArchived === true ? { includeArchived } : {}),
      ...(includeClosedBoards === true ? { includeClosedBoards } : {}),
      ...(matchDescriptions === false ? { matchDescriptions } : {}),
    };
  },
  component: SearchRoute,
});

function SearchRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t } = useInterfaceLanguage();
  const search = Route.useSearch();
  const q = stringValue(search.q) ?? '';
  const includeArchived = search.includeArchived === true;
  const includeClosedBoards = search.includeClosedBoards === true;
  const matchDescriptions = search.matchDescriptions !== false;
  const filters = searchSideFilterState(q);
  const enabled = q.length > 0;
  const [activeAdjacentProductProvider, setActiveAdjacentProductProvider] = useState<
    Parameters<typeof adjacentProductSearchQueryOptions>[0]['provider'] | undefined
  >(undefined);
  const resultQuery = useQuery({
    ...fullSearchResultsQueryOptions({
      q: enabled ? q : '_',
      includeArchived,
      includeClosedBoards,
      matchDescriptions,
    }),
    enabled,
  });
  const adjacentProductQuery = useQuery({
    ...adjacentProductSearchQueryOptions({
      q: enabled ? q : '_',
      ...(activeAdjacentProductProvider === undefined
        ? {}
        : { provider: activeAdjacentProductProvider }),
    }),
    enabled,
  });
  const savedSearchesQuery = useQuery(savedSearchesQueryOptions());
  const createSavedSearch = useCreateSavedSearch();
  const renameSavedSearch = useRenameSavedSearch();
  const deleteSavedSearch = useDeleteSavedSearch();
  const runSavedSearch = useRunSavedSearch();
  const cards = useMemo(() => resultQuery.data?.cards ?? [], [resultQuery.data?.cards]);
  const error =
    resultQuery.error instanceof ApiError
      ? resultQuery.error.message
      : resultQuery.isError
        ? t('search.tryAgainMoment')
        : undefined;
  const savedSearchesError =
    savedSearchesQuery.error instanceof ApiError
      ? savedSearchesQuery.error.message
      : savedSearchesQuery.isError
        ? t('search.tryAgainMoment')
        : undefined;
  const savedSearchActionError = [
    createSavedSearch.error,
    renameSavedSearch.error,
    deleteSavedSearch.error,
    runSavedSearch.error,
  ].find((candidate) => candidate !== null);
  const savedSearchActionErrorMessage =
    savedSearchActionError instanceof ApiError
      ? savedSearchActionError.message
      : savedSearchActionError instanceof Error
        ? savedSearchActionError.message
        : undefined;

  useEffect(() => {
    setActiveAdjacentProductProvider(undefined);
  }, [q]);

  useEffect(() => {
    if (activeAdjacentProductProvider === undefined) return;
    const tabs = adjacentProductQuery.data?.tabs;
    if (tabs === undefined) return;
    if (!tabs.some((tab) => tab.provider === activeAdjacentProductProvider)) {
      setActiveAdjacentProductProvider(undefined);
    }
  }, [activeAdjacentProductProvider, adjacentProductQuery.data?.tabs]);

  /**
   * ⚠ Every caller must carry `matchDescriptions` forward, and the one exception
   * is commented where it happens (`onRunSavedSearch`).
   *
   * `next` is the whole search state, not a patch — `searchHref` builds the URL
   * from it alone. Because `matchDescriptions` is absent from the URL when it is
   * `true`, a caller that omits it does not visibly clear anything: it silently
   * re-enables description matching the next time the reader types in the query
   * box or flips an unrelated side filter, leaving the checkbox unticked and
   * nothing red anywhere. `search.test.tsx` pins that directly.
   */
  function update(next: SearchRouteSearch) {
    void navigate({ href: searchHref(next) });
  }

  return (
    <SearchResultsPage
      query={q}
      cards={cards}
      loading={resultQuery.isLoading}
      {...(error === undefined ? {} : { error })}
      includeArchived={includeArchived}
      includeClosedBoards={includeClosedBoards}
      matchDescriptions={matchDescriptions}
      filters={filters}
      savedSearches={savedSearchesQuery.data?.savedSearches ?? []}
      savedSearchesLoading={savedSearchesQuery.isLoading}
      adjacentProductSearch={adjacentProductQuery.data ?? null}
      adjacentProductSearchLoading={adjacentProductQuery.isLoading}
      {...(savedSearchesError === undefined ? {} : { savedSearchesError })}
      {...(savedSearchActionErrorMessage === undefined
        ? {}
        : { savedSearchActionError: savedSearchActionErrorMessage })}
      {...(adjacentProductQuery.error instanceof ApiError
        ? { adjacentProductSearchError: adjacentProductQuery.error.message }
        : adjacentProductQuery.isError
          ? { adjacentProductSearchError: t('search.tryAgainMoment') }
          : {})}
      {...(activeAdjacentProductProvider === undefined
        ? {}
        : { activeAdjacentProductProvider })}
      savingSavedSearch={createSavedSearch.isPending}
      {...(renameSavedSearch.isPending
        ? { renamingSavedSearchId: renameSavedSearch.variables.id }
        : {})}
      {...(deleteSavedSearch.isPending
        ? { deletingSavedSearchId: deleteSavedSearch.variables }
        : {})}
      {...(runSavedSearch.isPending
        ? { runningSavedSearchId: runSavedSearch.variables }
        : {})}
      onQueryChange={(nextQuery) => {
        update({ q: nextQuery, includeArchived, includeClosedBoards, matchDescriptions });
      }}
      onIncludeArchivedChange={(next) => {
        update({ q, includeArchived: next, includeClosedBoards, matchDescriptions });
      }}
      onIncludeClosedBoardsChange={(next) => {
        update({ q, includeArchived, includeClosedBoards: next, matchDescriptions });
      }}
      onMatchDescriptionsChange={(next) => {
        update({ q, includeArchived, includeClosedBoards, matchDescriptions: next });
      }}
      onFiltersChange={(patch: Partial<SearchSideFilterState>) => {
        update({
          q: withSearchSideFilter(q, patch),
          includeArchived,
          includeClosedBoards,
          matchDescriptions,
        });
      }}
      onRetry={() => void resultQuery.refetch()}
      onSaveSearch={(name) => {
        // No `matchDescriptions`: `savedSearchCreateBodySchema` has no such field
        // and `saved_searches` has no such column. See `onRunSavedSearch` below.
        createSavedSearch.mutate({
          name,
          queryText: q,
          includeArchived,
          includeClosedBoards,
        });
      }}
      onRenameSavedSearch={(id, name) => {
        renameSavedSearch.mutate({ id, name });
      }}
      onDeleteSavedSearch={(id) => {
        deleteSavedSearch.mutate(id);
      }}
      onRunSavedSearch={(id) => {
        runSavedSearch.mutate(id, {
          onSuccess: ({ savedSearch, results }) => {
            queryClient.setQueryData(queryKeys.savedSearches.results(savedSearch.id), {
              savedSearch,
              results,
            });
            /**
             * ⚠ The run response is NOT seeded into `search.fullResults` any more.
             *
             * `savedSearchRunSchema.results` is the narrow `searchFullResultsSchema`
             * row — no `dueAt`, no `completed` — while the reader of that key now
             * expects the page row. Seeding it would put a badge-less row under a
             * key whose reader draws the badge: a card with a real due date would
             * render as though it had none, and nothing would be red. Fabricating
             * `dueAt: null, completed: false` onto the rows is the same defect with
             * a nicer type.
             *
             * Invalidating instead costs one request and keeps the rows honest,
             * because they then come from `GET /search/results` itself.
             *
             * The production client sets `staleTime: 30_000` (`main.tsx`) and
             * that does NOT suppress this refetch: `Query.isStaleByTime` returns
             * early on `state.isInvalidated`, so an invalidated entry is stale
             * whatever the clock says. Both orderings are therefore covered —
             * the run navigates to the SAME url the page is already on (a live
             * observer, refetched immediately by `invalidateQueries`' default
             * `refetchType: 'active'`) or to a different one (no observer yet,
             * refetched by `refetchOnMount` when the new one mounts).
             *
             * ⚠ `FR-SEARCH-029.spec.ts` AC4 depends on this path: it closes a board
             * while the results page is showing that board's card, runs the saved
             * search — same `q`/`includeArchived`/`includeClosedBoards`, therefore
             * the SAME url — and asserts the card disappears. The old
             * `setQueryData` overwrite is what made that pass; the invalidation has
             * to do it now, inside Playwright's auto-retry window.
             *
             * A saved search carries no third boolean (`saved_searches` has only
             * `include_archived` and `include_closed_boards`), so it re-runs at the
             * wire default — descriptions matched. Omitting `matchDescriptions`
             * from `update()` and passing `true` to the key below is deliberate,
             * not an oversight: threading the live toggle through here would make a
             * saved search mean something different depending on what the reader
             * had ticked when they ran it.
             */
            update({
              q: savedSearch.queryText,
              includeArchived: savedSearch.includeArchived,
              includeClosedBoards: savedSearch.includeClosedBoards,
            });
            void queryClient.invalidateQueries({
              queryKey: queryKeys.search.fullResults(
                savedSearch.queryText,
                savedSearch.includeArchived,
                savedSearch.includeClosedBoards,
                true,
              ),
            });
          },
        });
      }}
      onAdjacentProductProviderChange={(provider) => {
        setActiveAdjacentProductProvider(provider);
      }}
      onAdjacentProductRetry={() => void adjacentProductQuery.refetch()}
    />
  );
}
